import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type AIAssistantStatus =
  | 'sending'
  | 'thinking'
  | 'searching_web'
  | 'generating_response'
  | 'using_tools'
  | 'get_client_context'
  | 'get_company_context'
  | 'get_workspace_context'
  | 'taking_longer'
  | 'error';

export interface AIAssistantStatusState {
  statuses: Record<
    string,
    {
      status: AIAssistantStatus;
      metadata?: {
        tool?: string;
        query?: string;
        errorMessage?: string;
      };
      timestamp: number;
    }
  >;
}

const initialState: AIAssistantStatusState = {
  statuses: {},
};

interface SetStatusPayload {
  channelId: string;
  status: AIAssistantStatus;
  metadata?: {
    tool?: string;
    query?: string;
    errorMessage?: string;
  };
}

interface ClearStatusPayload {
  channelId: string;
}

const aiAssistantStatusSlice = createSlice({
  name: 'aiAssistantStatus',
  initialState,
  reducers: {
    setStatus: (state, action: PayloadAction<SetStatusPayload>) => {
      const { channelId, status, metadata } = action.payload;
      state.statuses[channelId] = {
        status,
        metadata,
        timestamp: Date.now(),
      };
    },
    clearStatus: (state, action: PayloadAction<ClearStatusPayload>) => {
      const { channelId } = action.payload;
      delete state.statuses[channelId];
    },
    clearAllStatuses: (state) => {
      state.statuses = {};
    },
  },
});

// Selectors
export const selectStatusByChannelId = (
  state: { aiAssistantStatus: AIAssistantStatusState },
  channelId?: string,
) => {
  if (!channelId) return null;
  return state.aiAssistantStatus.statuses[channelId] || null;
};

export const selectHasActiveStatus = (
  state: { aiAssistantStatus: AIAssistantStatusState },
  channelId?: string,
) => {
  if (!channelId) return false;
  return !!state.aiAssistantStatus.statuses[channelId];
};

export const { setStatus, clearStatus, clearAllStatuses } =
  aiAssistantStatusSlice.actions;

export default aiAssistantStatusSlice.reducer;
